import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaEnvelope, FaCalendarAlt } from 'react-icons/fa';

const Contact = () => {
  const { t } = useTranslation();
  
  // Custom states to handle structured input
  const [formData, setFormData] = useState({
    to_name: '',
    from_name: '',
    budget: '',
    timeline: '',
    requirements: '',
    custom_message: ''
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);

    // Combine structured details into a clean email body format for the EmailJS 'message' key
    const combinedMessage = `
=== SOFTKEL BUSINESS INQUIRY ===
Name: ${formData.to_name}
Email: ${formData.from_name}
Budget Range: ${formData.budget || 'Not specified'}
Expected Timeline: ${formData.timeline || 'Not specified'}
Core Requirements: ${formData.requirements || 'Not specified'}

Additional Details:
${formData.custom_message}
    `.trim();

    // Create a temporary form payload to submit
    const tempForm = document.createElement('form');
    
    const toNameInput = document.createElement('input');
    toNameInput.name = 'to_name';
    toNameInput.value = formData.to_name;
    
    const fromNameInput = document.createElement('input');
    fromNameInput.name = 'from_name';
    fromNameInput.value = formData.from_name;
    
    const messageInput = document.createElement('textarea');
    messageInput.name = 'message';
    messageInput.value = combinedMessage;

    tempForm.appendChild(toNameInput);
    tempForm.appendChild(fromNameInput);
    tempForm.appendChild(messageInput);

    document.body.appendChild(tempForm);

    emailjs.sendForm('service_jw8uvjc', 'template_0vuhize', tempForm, '4H5SK0NmSmTwTUw6v')
      .then((result) => {
          console.log(result.text);
          toast.success("Message sent successfully!");
          setFormData({
            to_name: '',
            from_name: '',
            budget: '',
            timeline: '',
            requirements: '',
            custom_message: ''
          });
          document.body.removeChild(tempForm);
          setLoading(false);
      }, (error) => {
          toast.error("Failed to send message. Please try again.");
          console.error(error.text);
          document.body.removeChild(tempForm);
          setLoading(false);
      });
  };

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative min-h-screen flex items-center justify-center">
      {/* Radiant Gradient Blurs */}
      <div className="absolute top-10 left-1/4 w-80 h-80 rounded-full bg-blue-500/5 dark:bg-blue-600/5 blur-3xl" />
      <div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full bg-purple-500/5 dark:bg-purple-600/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
        {/* Title */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('contactLabel', 'Get In Touch')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('feelFreeToContact')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
          <p className="mt-4 text-slate-500 dark:text-neutral-400 max-w-xl mx-auto text-sm sm:text-base">
            {t('feelWelcomeToReachOut')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start max-w-5xl mx-auto">
          {/* Left: Info & Quick Action Buttons */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <h3 className="text-xl font-extrabold text-slate-900 dark:text-white mb-4">
                {t('lets_talk_title', 'Let’s Discuss Your Idea')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-neutral-400 leading-relaxed">
                {t('contactUs', "Contact Us Today, Let's Connect, Discuss, and Fulfill Your Needs with Excellence.")}
              </p>
            </div>

            {/* Quick Actions Links Grid */}
            <div className="flex flex-col gap-4">
              <a
                href="https://wa.me/358417298399"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 hover:scale-[1.02] transition-all duration-300"
              >
                <FaWhatsapp className="w-6 h-6" />
                <div className="text-left">
                  <h4 className="font-extrabold text-sm">WhatsApp Chat</h4>
                  <p className="text-xs opacity-80">+358 41 7298399</p>
                </div>
              </a>

              <a
                href="mailto:info@softkel.com"
                className="flex items-center gap-4 p-4 rounded-2xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:scale-[1.02] transition-all duration-300"
              >
                <FaEnvelope className="w-6 h-6" />
                <div className="text-left">
                  <h4 className="font-extrabold text-sm">Direct Email</h4>
                  <p className="text-xs opacity-80">info@softkel.com</p>
                </div>
              </a>

              {/* <a
                href="https://calendly.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 border border-purple-500/20 hover:scale-[1.02] transition-all duration-300"
              >
                <FaCalendarAlt className="w-6 h-6" />
                <div className="text-left">
                  <h4 className="font-extrabold text-sm">Schedule Meeting</h4>
                  <p className="text-xs opacity-80">Book a slot on Google Calendar</p>
                </div>
              </a> */}
            </div>
          </div>

          {/* Right: Glassmorphism Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <form 
              onSubmit={sendEmail}
              className="p-8 rounded-3xl bg-slate-50/50 dark:bg-[#111111]/30 border border-slate-200/50 dark:border-neutral-800/40 backdrop-blur-md shadow-2xl flex flex-col gap-6"
            >
              {/* Full Name */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                  {t('fullName')} *
                </label>
                <input
                  type="text"
                  name="to_name"
                  value={formData.to_name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('typeHere')}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                  {t('email')} *
                </label>
                <input
                  type="email"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleInputChange}
                  required
                  placeholder="name@company.com"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm"
                />
              </div>

              {/* Budget & Timeline layout */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                    Budget Scope
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm"
                  >
                    <option value="" disabled hidden className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Select Range</option>
                    <option value="<$5k" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Under $5k</option>
                    <option value="$5k-$15k" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">$5k - $15k</option>
                    <option value="$15k-$50k" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">$15k - $50k</option>
                    <option value="$50k+" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">$50k & Above</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                    Expected Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm"
                  >
                    <option value="" disabled hidden className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Select Timeline</option>
                    <option value="<1mo" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Under 1 Month</option>
                    <option value="1-3mo" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">1 to 3 Months</option>
                    <option value="3-6mo" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">3 to 6 Months</option>
                    <option value="6mo+" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">6 Months & Above</option>
                  </select>
                </div>
              </div>

              {/* Requirements tags select */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                  Key Requirements
                </label>
                <select
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm"
                >
                  <option value="" disabled hidden className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Select Area</option>
                  <option value="Mobile App Dev" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Mobile App Development</option>
                  <option value="Web App Dev" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Web App Development</option>
                  <option value="Full-stack / Cloud" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Backend / Cloud Systems</option>
                  <option value="ERP / SAP Customize" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">ERP & SAP Integration</option>
                  <option value="Digital Transform / Consulting" className="bg-white dark:bg-[#111111] text-slate-800 dark:text-slate-100">Consulting / Emerging Tech</option>
                </select>
              </div>

              {/* Custom Message Details */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">
                  {t('message')} *
                </label>
                <textarea
                  name="custom_message"
                  value={formData.custom_message}
                  onChange={handleInputChange}
                  required
                  placeholder={t('budgetScopeTimelineRequirements', 'Please tell us more about your project idea...')}
                  className="w-full h-32 px-4 py-3 rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-[#080808] text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-purple-500/50 transition-all text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 text-sm font-bold text-white uppercase tracking-wider rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all shadow-md shadow-blue-500/10 dark:shadow-purple-500/10 hover:shadow-lg active:scale-[0.98]"
              >
                {loading ? 'Sending...' : t('submit')}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
      <ToastContainer position="bottom-right" theme={typeof window !== "undefined" && document.documentElement.classList.contains('dark') ? 'dark' : 'light'} />
    </section>
  );
};

export default Contact;
export { Contact };